// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import loadCurrentShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTo";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import saveHubSpotInfo from "@west/App/Handlers/HubSpot/SaveHubSpotInfo";
import loadSystemList from "@west/App/Handlers/SystemList/LoadSystemList";
import { SystemListModel } from "@west/App/Services/SystemListService";
import { CustomAppState } from "@west/App/Store/Reducers";
import { getSystemList } from "@west/App/Store/Selectors/SystemListSelectors";
import Select, { SelectPresentationProps } from "@west/MobiusOverrides/Select";
import TextField, { TextFieldPresentationProps } from "@west/MobiusOverrides/TextField";
import { XNGAGE_CUSTOM_WIDGET } from "@west/XDL/constants";
import { useValidate } from "@west/XDL/Utilities";
import isEmpty from "lodash/isEmpty";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const StyledForm = getStyledWrapper("form");

// Note: looks like the system lists are coming back from BE as lowerCase
const mapStateToProps = (state: CustomAppState) => ({
    userEmail: getSession(state).email,
    billToState: getCurrentBillToState(state),
    companyTypesList: getSystemList(state, "hubSpotIndustries"),
    numberOfEmployeesList: getSystemList(state, "hubSpotNumberOfEmployees"),
    frequentlyPurchasedList: getSystemList(state, "hubSpotFrequentlyPurchased"),
    currentShipToState: getCurrentShipToState(state),
});

const mapDispatchToProps = {
    loadCurrentBillTo,
    loadSystemList,
    saveHubSpotInfo,
    loadCurrentShipTo,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export type HubSpotAdditionalInfoFormFields = {
    phoneNumber?: string;
    jobTitle?: string;
    companyOwner?: string;
    companyType?: string;
    licensingNum?: string;
    websiteUrl?: string;
    numberOfEmployees?: string;
    frequentlyPurchasedProducts?: string[];
    email: string;
    customerNumber: string;
};

export interface HubSpotAdditionalInfoFormStyles {
    form?: InjectableCss;
    formGridContainer?: GridContainerProps;
    phoneNumberTextField?: TextFieldPresentationProps;
    jobTitleTextField?: TextFieldPresentationProps;
    companyOwnerTextField?: TextFieldPresentationProps;
    companyTypeSelect?: SelectPresentationProps;
    licensingTextField?: TextFieldPresentationProps;
    websiteTextField?: TextFieldPresentationProps;
    numOfEmployeesSelect?: SelectPresentationProps;
    hubSpotAdditionalInfoFormSubmitButton?: ButtonPresentationProps;
    fullWidthGridItem?: GridItemProps;
    halfWidthGridItem?: GridItemProps;
    frequentlyPurchasedCheckboxGroup?: CheckboxGroupComponentProps;
    frequentlyPurchasedCheckbox?: CheckboxPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const hubSpotAdditionalInfoFormStyles: HubSpotAdditionalInfoFormStyles = {};

const styles = hubSpotAdditionalInfoFormStyles;

const HubSpotAdditionalInfoForm: React.FunctionComponent<Props> = ({
    billToState,
    userEmail,
    companyTypesList,
    numberOfEmployeesList,
    frequentlyPurchasedList,
    loadSystemList,
    loadCurrentBillTo,
    saveHubSpotInfo,
    currentShipToState,
    loadCurrentShipTo,
}: Props) => {
    const toasterContext = React.useContext(ToasterContext);
    const { field, handleSubmit, isSubmitting, values, setFieldValue, errors } = useValidate({
        initialValues: {
            phoneNumber: "",
            jobTitle: "",
            companyOwner: "",
            companyType: "",
            licensingNumber: "",
            websiteUrl: "",
            numberOfEmployees: "",
            frequentlyPurchasedProducts: [],
        },
        validators: {
            phoneNumber: "phone",
            jobTitle: "max:100",
            companyOwner: "max:100",
            companyType: "max:80",
            licensingNumber: "max:100",
            websiteUrl: "url|max:2048",
            numberOfEmployees: "",
        },
        enableReinitialize: true,
        onSubmit: (fieldValues: HubSpotAdditionalInfoFormFields, { setSubmitting }: any) => {
            submitHandler(fieldValues, setSubmitting);
        },
    });

    useEffect(() => {
        if (!billToState.value && !billToState.isLoading) {
            loadCurrentBillTo();
        }

        const listNames = [];
        if (!companyTypesList) {
            listNames.push("HubSpotIndustries");
        }

        if (!numberOfEmployeesList) {
            listNames.push("HubSpotNumberOfEmployees");
        }

        if (!frequentlyPurchasedList) {
            listNames.push("HubSpotFrequentlyPurchased");
        }

        loadSystemList({ listNames: listNames.join(",") });

        if (!currentShipToState.value && !currentShipToState.isLoading) {
            loadCurrentShipTo();
        }
    }, []);

    if (billToState.isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (!billToState.value) {
        return null;
    }

    const submitHandler = (fieldValues: HubSpotAdditionalInfoFormFields, setSubmitting: any) => {
        setSubmitting(true);

        saveHubSpotInfo({
            ...fieldValues,
            email: userEmail,
            customerNumber: billToState.value.customerNumber,
            frequentlyPurchasedProducts: fieldValues?.frequentlyPurchasedProducts?.join(";") || "",
            onSuccess: () => {
                toasterContext.addToast({ body: translate("Form Info has been saved"), messageType: "success" });
                setSubmitting(false);
            },
            onError: (errorMessage: string) => {
                toasterContext.addToast({ body: translate(errorMessage), messageType: "danger" });
                setSubmitting(false);
            },
        });
    };

    /**
     * by default, all fields value are empty. so we check if any field has a value (not empty) so there are some changes in the form
     * so in this case the user can submit the form.
     * if the hasChanges is false, we disabled the submit button.
     */
    const hasChanges =
        values.phoneNumber !== "" ||
        values.jobTitle !== "" ||
        values.companyOwner !== "" ||
        values.companyType !== "" ||
        values.licensingNumber !== "" ||
        values.websiteUrl !== "" ||
        values.numberOfEmployees !== "" ||
        values.frequentlyPurchasedProducts.length > 0;

    const onFrequentlyPurchasedProductsChangeHandler = (newValue: string) => {
        const fieldName = "frequentlyPurchasedProducts";

        const isExists = field(fieldName).value.includes(newValue);

        const newFieldValue = isExists
            ? field(fieldName).value.filter((itemName: string) => itemName !== newValue)
            : [...field(fieldName).value, newValue];

        setFieldValue(fieldName, newFieldValue);
    };

    // Sorting dropdown lists options
    // sort based on numbers
    const sortedNumberOfEmployeesList = numberOfEmployeesList
        ? [...numberOfEmployeesList].sort((a, b) => Number(a.additionalInfo) - Number(b.additionalInfo))
        : [];
    // sort based on a string (alphabetically)
    const sortedCompanyTypesList = companyTypesList
        ? [...companyTypesList].sort((a, b) => a.name.localeCompare(b.name))
        : [];
    const sortedFrequentlyPurchasedList = frequentlyPurchasedList
        ? [...frequentlyPurchasedList].sort((a, b) => a.name.localeCompare(b.name))
        : [];

    const validation = currentShipToState?.value?.validation;
    return (
        <StyledForm {...styles.form} onSubmit={handleSubmit} noValidate data-test-selector="hubSpotAdditionalInfoForm">
            <GridContainer>
                <GridItem {...styles.fullWidthGridItem}>
                    <TextField
                        {...styles.phoneNumberTextField}
                        label={translate("Phone Number")}
                        placeholder={translate("Enter Your Phone Number")}
                        {...field("phoneNumber")}
                        data-test-selector="hubSpotAdditionalInfoForm_phoneNumber"
                        maxLength={validation?.phone?.maxLength || undefined}
                    />
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    <TextField
                        {...styles.jobTitleTextField}
                        label={translate("Job Title")}
                        placeholder={translate("Enter Your Job Title")}
                        {...field("jobTitle")}
                        data-test-selector="hubSpotAdditionalInfoForm_jobTitle"
                    />
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    <TextField
                        {...styles.companyOwnerTextField}
                        label={translate("Company Owner")}
                        placeholder={translate("Enter Company Owner")}
                        {...field("companyOwner")}
                        data-test-selector="hubSpotAdditionalInfoForm_companyOwner"
                    />
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    <Select
                        {...styles.companyTypeSelect}
                        label={translate("Company Type")}
                        placeholder={translate("Select Company Type")}
                        {...field("companyType")}
                        data-test-selector="hubSpotAdditionalInfoForm_companyTypeSelect"
                    >
                        <option value="">{translate("Select Type")}</option>
                        {sortedCompanyTypesList?.map(type => (
                            <option key={type.name} value={type.name}>
                                {type.description || type.name}
                            </option>
                        ))}
                    </Select>
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    <TextField
                        {...styles.licensingTextField}
                        label={translate("Licensing #")}
                        placeholder={translate("Enter Your Licensing #")}
                        {...field("licensingNumber")}
                        data-test-selector="hubSpotAdditionalInfoForm_licensingNumber"
                    />
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    <TextField
                        {...styles.websiteTextField}
                        label={translate("Website URL")}
                        placeholder={translate("Enter Website URL")}
                        {...field("websiteUrl")}
                        data-test-selector="hubSpotAdditionalInfoForm_websiteUrl"
                        type="url"
                    />
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    <Select
                        {...styles.numOfEmployeesSelect}
                        label={translate("Number of Employees")}
                        placeholder={translate("Select Number of Employees")}
                        {...field("numberOfEmployees")}
                        data-test-selector="hubSpotAdditionalInfoForm_numberOfEmployeesSelect"
                    >
                        <option value="">{translate("Select Employee Range")}</option>
                        {sortedNumberOfEmployeesList?.map(range => (
                            <option key={range.name} value={range.name}>
                                {range.description || range.name}
                            </option>
                        ))}
                    </Select>
                </GridItem>
                {frequentlyPurchasedList && frequentlyPurchasedList.length > 0 && (
                    <GridItem {...styles.fullWidthGridItem}>
                        <CheckboxGroup
                            {...styles.frequentlyPurchasedCheckboxGroup}
                            label={translate("Frequently Purchased Products")}
                        >
                            {sortedFrequentlyPurchasedList?.map(item => (
                                <Checkbox
                                    {...styles.frequentlyPurchasedCheckbox}
                                    key={item.name}
                                    checked={field("frequentlyPurchasedProducts").value.includes(item.name)}
                                    onChange={_ => onFrequentlyPurchasedProductsChangeHandler(item.name)}
                                    data-test-selector="hubSpotAdditionalInfoForm_frequentlyPurchasedCheckbox"
                                >
                                    {item.description || item.name}
                                </Checkbox>
                            ))}
                        </CheckboxGroup>
                    </GridItem>
                )}
                <GridItem {...styles.fullWidthGridItem}>
                    <Button
                        aria-label={translate("Save")}
                        data-test-selector="hubSpot_submitButton"
                        type="submit"
                        disabled={isSubmitting || !hasChanges || !isEmpty(errors)}
                        {...styles.hubSpotAdditionalInfoFormSubmitButton}
                    >
                        {translate("Save")}
                    </Button>
                </GridItem>
            </GridContainer>
        </StyledForm>
    );
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(HubSpotAdditionalInfoForm),
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
    },
};

export default widgetModule;
